import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormHelperText,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  ArrowBack,
  CallSplit,
  LockOpen,
  CreditCard,
  Group,
  Link as LinkIcon,
  MoreVert,
  OpenInNew,
  PhoneIphone,
  Place,
  Star,
  PlaylistAdd,
  StarBorder,
  Storefront,
  VpnKey,
  Password,
} from '@mui/icons-material';
import apiService from '../services/apiService';
import { colors } from '../theme/theme';
import BrandSettingsTab from '../components/BrandSettingsTab';
import BrandAccessList from '../components/BrandAccessList';
import BrandSharingTab from '../components/BrandSharingTab';

interface Branch {
  _id: string;
  studioName: string;
  branchName?: string | null;
  branchCode?: string | null;
  email?: string;
  phoneNumber?: string;
  city?: string;
  address?: string;
  isActive: boolean;
  branchIsActive?: boolean;
}

interface Sharing {
  packs: boolean;
  benefits: boolean;
  referrals: boolean;
  announcements: boolean;
}

interface Brand {
  _id: string;
  name: string;
  slug: string;
  logo?: string;
  appSource?: string;
  defaultStudio?: string | null;
  supportEmail?: string;
  supportPhone?: string;
  status: 'active' | 'suspended';
  sharing: Sharing;
  hasOwnGateway?: boolean;
}

interface Grant {
  _id: string;
  user: { kind: 'staff' | 'studio' | 'admin'; id: string };
  role: 'brand_owner' | 'studio_owner' | 'manager' | 'instructor';
  scope: { kind: 'brand' | 'studio'; brand: string; studio?: any };
  status: 'active' | 'revoked';
}

interface PersonGrant {
  _id: string;
  role: Grant['role'];
  status: 'active' | 'revoked';
  scopeKind: 'brand' | 'studio';
  studio: any;
}

interface AccessPerson {
  key: string;
  kind: 'staff' | 'studio';
  id: string;
  name: string;
  email: string | null;
  isActive: boolean;
  note: string | null;
  missing: boolean;
  role: Grant['role'] | null;
  activeCount: number;
  grants: PersonGrant[];
}

interface Person {
  kind: 'staff' | 'studio';
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  locations: string[];
  current: { role: string; where: string } | null;
  note: string | null;
}

interface Adoptable {
  _id: string;
  studioName: string;
  city?: string | null;
  appSource?: string | null;
}

const ROLE_LABEL: Record<string, string> = {
  brand_owner: 'Brand owner',
  studio_owner: 'Studio owner',
  manager: 'Manager',
  instructor: 'Instructor',
};

/** Location | City | Login | actions — same rhythm as the Access table. */
const LOCATION_GRID = 'minmax(0, 2.5fr) minmax(0, 1fr) 130px 80px';

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const BrandDetailPage: React.FC = () => {
  const { brandId = '' } = useParams();
  const navigate = useNavigate();

  const [brand, setBrand] = useState<Brand | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [accessPeople, setAccessPeople] = useState<AccessPerson[]>([]);
  const [showRevoked, setShowRevoked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  // new branch
  const [branchOpen, setBranchOpen] = useState(false);
  const [wantsLogin, setWantsLogin] = useState(false);
  /** Set when the dialog was opened via Duplicate, purely so the copy reads right. */
  const [duplicateOf, setDuplicateOf] = useState<Branch | null>(null);
  const [branchForm, setBranchForm] = useState({
    studioName: '', branchName: '', branchCode: '', city: '',
    address: '', phoneNumber: '', email: '', password: '',
    // Only asked for a brand's FIRST location; later ones copy the default's.
    studioType: 'workout' as 'workout' | 'wellness',
  });
  /** Same Razorpay account as the default location — off unless chosen. */
  const [copyPayments, setCopyPayments] = useState(false);
  const [branchError, setBranchError] = useState<string | null>(null);
  const branchErrorRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (branchError) branchErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [branchError]);

  /** Shown once after a login is created or a password is (re)generated. */
  const [credentials, setCredentials] = useState<{
    title: string; location: string; email: string; password?: string; note?: string;
  } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  // location login: create / reset password
  const [loginFor, setLoginFor] = useState<Branch | null>(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '', mode: 'generate' as 'generate' | 'type' });
  const [loginSaving, setLoginSaving] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // adopt existing
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [adoptable, setAdoptable] = useState<Adoptable[]>([]);
  const [adoptPick, setAdoptPick] = useState<Adoptable | null>(null);
  const [adoptSearch, setAdoptSearch] = useState('');

  const [saving, setSaving] = useState(false);

  // grants
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForm, setGrantForm] = useState<{
    kind: 'staff' | 'studio'; userId: string; role: Grant['role']; studio: string;
  }>({ kind: 'staff', userId: '', role: 'manager', studio: '' });
  /** Locations for a studio-scoped grant. One row is written per location. */
  const [grantStudios, setGrantStudios] = useState<string[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [person, setPerson] = useState<Person | null>(null);
  /** "pick" an existing login, or "new" — create one and grant in one go. */
  const [grantMode, setGrantMode] = useState<'pick' | 'new'>('pick');
  const [newPerson, setNewPerson] = useState({ name: '', email: '', password: '', phone: '' });
  const [revoking, setRevoking] = useState<any | null>(null);
  /** Remove every grant a person holds in this brand, in one confirmation. */
  const [removingAll, setRemovingAll] = useState<AccessPerson | null>(null);
  /** The person whose locations are being edited, and the working selection. */
  const [editingLocations, setEditingLocations] = useState<AccessPerson | null>(null);
  const [editLocations, setEditLocations] = useState<string[]>([]);

  // per-branch menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuBranch, setMenuBranch] = useState<Branch | null>(null);
  const [detaching, setDetaching] = useState<Branch | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, g]: any[] = await Promise.all([
        apiService.getBrand(brandId),
        apiService.getBrandGrants(brandId),
      ]);
      setBrand(detail.brand);
      setBranches(detail.studios ?? []);
      setGrants(g?.grants ?? []);
      setAccessPeople(g?.people ?? []);
    } catch (e: any) {
      setError(e.message || 'Could not load this brand');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const activeGrants = useMemo(() => grants.filter((g) => g.status === 'active'), [grants]);

  /**
   * The app-level half of `whitelabelConfig`, read off any one branch.
   *
   * They are kept identical by `saveBrandWhitelabel` below, so reading the
   * first is the same as reading any — and it is why there is no such field
   * on the Brand document to read instead. See brand-whitelabel.fields.ts.
   */
  // Memoised so the settings form only resets when the data really changes.
  const brandWhitelabel: Record<string, any> = useMemo(
    () => (branches?.[0] as any)?.whitelabelConfig ?? {},
    [branches],
  );

  const patch = async (data: any, message: string) => {
    setError(null);
    try {
      const res: any = await apiService.updateBrand(brandId, data);
      setBrand(res.brand);
      setBranches(res.studios ?? []);
      setNotice(message);
    } catch (e: any) {
      setError(e.message || 'Could not save');
    }
  };

  /**
   * Duplicate — the same shape as duplicating an event.
   *
   * Opens the ordinary create dialog with the source location's details filled
   * in, and nothing is written until the operator saves. Identity is
   * deliberately NOT copied: a second location needs its own name, its own
   * code and its own address, and silently inheriting them produces two rows
   * nobody can tell apart.
   *
   * What it saves is the tedious part — the address block, the city, the
   * phone — which for a second location in the same neighbourhood is usually
   * most of the form.
   */
  const duplicate = (b: Branch) => {
    setDuplicateOf(b);
    setWantsLogin(false);
    setBranchForm({
      studioName: `${b.studioName} (copy)`,
      branchName: '',
      branchCode: '',
      city: b.city ?? '',
      address: b.address ?? '',
      phoneNumber: b.phoneNumber ?? '',
      email: '',
      password: '',
      studioType: 'workout',
    });
    setBranchError(null); setCopyPayments(false);
    setBranchOpen(true);
  };

  const openNewBranch = () => {
    setDuplicateOf(null);
    setWantsLogin(false);
    setBranchForm({
      studioName: '', branchName: '', branchCode: '', city: '',
      address: '', phoneNumber: '', email: '', password: '',
      studioType: 'workout',
    });
    setBranchError(null); setCopyPayments(false);
    setBranchOpen(true);
  };

  const createBranch = async () => {
    setBranchError(null);
    if (wantsLogin) {
      if (!/^\S+@\S+\.\S+$/.test(branchForm.email.trim())) {
        setBranchError('Enter a valid email for the location’s login.');
        return;
      }
      if (branchForm.password && branchForm.password.length < 8) {
        setBranchError('The password must be at least 8 characters, or leave it blank to generate one.');
        return;
      }
    }
    setSaving(true);
    try {
      const res: any = await apiService.createBranch(brandId, {
        studioName: branchForm.studioName.trim(),
        branchName: branchForm.branchName.trim() || undefined,
        branchCode: branchForm.branchCode.trim() || undefined,
        city: branchForm.city.trim() || undefined,
        address: branchForm.address.trim() || undefined,
        phoneNumber: branchForm.phoneNumber.trim() || undefined,
        email: wantsLogin ? branchForm.email.trim() : undefined,
        password: wantsLogin && branchForm.password ? branchForm.password : undefined,
        copyPaymentAccount: branches.length > 0 ? copyPayments : undefined,
        // A first location has nothing to inherit a type from, and a studio
        // with none is invisible to discovery and every category filter.
        ...(branches.length === 0 ? { studioType: branchForm.studioType } : {}),
      } as any);
      const out = res?.data ?? res;
      const name = out?.branch?.branchName || out?.branch?.studioName || branchForm.studioName.trim();
      setBranchOpen(false); setWantsLogin(false); setCopyPayments(false);
      setBranchForm({ studioName: '', branchName: '', branchCode: '', city: '', address: '', phoneNumber: '', email: '', password: '', studioType: 'workout' });
      setNotice(`${name} added`);
      if (out?.login?.email) {
        setCredentials({
          title: 'Location created',
          location: name,
          email: out.login.email,
          password: out.login.password,
          note: out.login.password ? undefined : 'Signs in with the password you typed.',
        });
      }
      load();
    } catch (e: any) {
      setBranchError(e?.response?.data?.message || e?.message || 'Could not create the location. Please try again.');
    } finally { setSaving(false); }
  };

  const openLogin = (b: Branch) => {
    setLoginFor(b);
    setLoginForm({ email: '', password: '', mode: 'generate' });
    setLoginError(null);
  };

  const saveLogin = async () => {
    if (!loginFor) return;
    setLoginError(null);
    const needsEmail = !loginFor.email;
    if (needsEmail && !/^\S+@\S+\.\S+$/.test(loginForm.email.trim())) {
      setLoginError('Enter a valid email for the new login.');
      return;
    }
    if (loginForm.mode === 'type' && loginForm.password.length < 8) {
      setLoginError('The password must be at least 8 characters.');
      return;
    }
    setLoginSaving(true);
    try {
      const res: any = await apiService.setBranchLogin(brandId, loginFor._id, {
        ...(needsEmail ? { email: loginForm.email.trim() } : {}),
        ...(loginForm.mode === 'type' ? { password: loginForm.password } : {}),
      });
      const out = res?.data ?? res;
      const name = loginFor.branchName || loginFor.studioName;
      setLoginFor(null);
      setNotice(out?.action === 'login_created' ? `Login created for ${name}` : `Password reset for ${name}`);
      setCredentials({
        title: out?.action === 'login_created' ? 'Login created' : 'Password reset',
        location: name,
        email: out?.login?.email,
        password: out?.login?.password,
        note: [
          out?.login?.password ? '' : 'Signs in with the password you typed.',
          out?.loginKind === 'studio' ? 'This is the location’s own email login.' : '',
          out?.inactive ? 'This login is switched off. Turn it back on from the Access tab before the owner can sign in.' : '',
        ].filter(Boolean).join(' ') || undefined,
      });
      load();
    } catch (e: any) {
      setLoginError(e?.response?.data?.message || e?.message || 'Could not update the login. Please try again.');
    } finally {
      setLoginSaving(false);
    }
  };

  /**
   * Searched SERVER-side, not filtered in the browser.
   *
   * The endpoint used to return every studio on the platform and the
   * Autocomplete narrowed them locally. That is fine at ninety-four and a
   * multi-megabyte response at ten thousand — and once the server started
   * capping the list, local filtering would have quietly searched only the
   * first page.
   */
  const loadAdoptable = useCallback(async (term: string) => {
    try {
      const res: any = await apiService.getAdoptableStudios(brandId, term || undefined);
      setAdoptable(res?.studios ?? []);
    } catch (e: any) {
      setError(e.message || 'Could not load studios');
    }
  }, [brandId]);

  useEffect(() => {
    if (!adoptOpen) return;
    const t = setTimeout(() => loadAdoptable(adoptSearch), adoptSearch ? 300 : 0);
    return () => clearTimeout(t);
  }, [adoptOpen, adoptSearch, loadAdoptable]);

  const openAdopt = () => {
    setAdoptOpen(true);
    setAdoptPick(null);
    setAdoptSearch('');
    setError(null);
  };

  const adopt = async () => {
    if (!adoptPick) return;
    setSaving(true); setError(null);
    try {
      await apiService.adoptStudioIntoBrand(brandId, adoptPick._id);
      setAdoptOpen(false);
      setNotice(`${adoptPick.studioName} joined this brand`);
      load();
    } catch (e: any) {
      setError(e.message || 'Could not move that studio');
    } finally { setSaving(false); }
  };

  const detach = async () => {
    if (!detaching) return;
    try {
      await apiService.detachStudioFromBrand(brandId, detaching._id);
      setNotice(`${detaching.studioName} now has its own brand`);
      setDetaching(null);
      load();
    } catch (e: any) {
      setError(e.message || 'Could not remove that location');
      setDetaching(null);
    }
  };

  const toggleBranch = async (b: Branch) => {
    try {
      await apiService.setBranchActive(brandId, b._id, b.branchIsActive === false);
      load();
    } catch (e: any) { setError(e.message || 'Could not change the location'); }
  };

  const openGrant = async () => {
    setGrantOpen(true);
    setError(null);
    setPerson(null);
    setGrantMode('pick');
    setGrantStudios([]);
    setNewPerson({ name: '', email: '', password: '', phone: '' });
    try {
      const res: any = await apiService.getBrandPeople(brandId);
      setPeople(res?.people ?? []);
    } catch (e: any) {
      setError(e.message || 'Could not load people');
    }
  };

  const submitGrant = async () => {
    setSaving(true); setError(null);
    try {
      let kind: 'staff' | 'studio' = person?.kind ?? 'staff';
      let userId = person?.id ?? '';

      if (grantMode === 'new') {
        // A brand owner usually does not exist yet — creating the login and
        // granting it are one intention, so they are one action. The login is
        // created at whichever location is named (or the brand's default for
        // a brand-wide grant); the GRANT is what decides their reach.
        const home =
          grantForm.role === 'brand_owner'
            ? (String(brand?.defaultStudio || '') || branches[0]?._id)
            : grantStudios[0];
        const created: any = await apiService.createStudioUser({
          name: newPerson.name.trim(),
          email: newPerson.email.trim(),
          password: newPerson.password,
          phone: newPerson.phone.trim() || undefined,
          role: grantForm.role,
          studioId: home,
        });
        const u = created?.studioUser ?? created?.user ?? created;
        userId = u?._id;
        kind = 'staff';
        if (!userId) throw new Error('The login was not created');
      }

      await apiService.grantBrandAccess(brandId, {
        kind,
        userId,
        role: grantForm.role,
        studios: grantForm.role === 'brand_owner' ? undefined : grantStudios,
      });
      setGrantOpen(false);
      setGrantForm({ kind: 'staff', userId: '', role: 'manager', studio: '' });
      setGrantStudios([]);
      setPerson(null);
      setNotice('Access granted');
      load();
    } catch (e: any) { setError(e.message || 'Could not grant access'); }
    finally { setSaving(false); }
  };

  const openLocationEditor = (p: AccessPerson) => {
    setEditingLocations(p);
    setEditLocations(
      p.grants
        .filter((g) => g.status === 'active' && g.scopeKind === 'studio')
        .map((g) => String(g.studio?._id ?? g.studio)),
    );
  };

  const saveLocations = async () => {
    if (!editingLocations?.role) return;
    setSaving(true);
    setError(null);
    try {
      const res: any = await apiService.setBrandGrantLocations(brandId, {
        kind: editingLocations.kind,
        userId: String(editingLocations.id),
        role: editingLocations.role as any,
        studios: editLocations,
      });
      setNotice(`${editingLocations.name}: ${res?.message ?? 'saved'}`);
      setEditingLocations(null);
      load();
    } catch (e: any) {
      setError(e.message || 'Could not change their locations');
    } finally {
      setSaving(false);
    }
  };

  const removeAllAccess = async () => {
    if (!removingAll) return;
    try {
      // One call per grant. Revoking is per-row by design — that is what makes
      // removing ONE location a single operation — so removing everything is
      // just all of them.
      for (const g of removingAll.grants.filter((x) => x.status === 'active')) {
        await apiService.revokeBrandAccess(brandId, g._id);
      }
      setNotice(`${removingAll.name} no longer has access`);
      setRemovingAll(null);
      load();
    } catch (e: any) {
      setError(e.message || 'Could not remove access');
      setRemovingAll(null);
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    try {
      await apiService.revokeBrandAccess(brandId, revoking._id);
      setRevoking(null); setNotice('Access revoked'); load();
    } catch (e: any) { setError(e.message || 'Could not revoke access'); }
  };

  /** Why Grant is unavailable, or null when it is ready. */
  const grantBlockedBecause = (() => {
    if (grantForm.role !== 'brand_owner' && !grantStudios.length) return 'Pick at least one location';
    if (grantMode === 'pick') return person ? null : 'Pick a person';
    if (!newPerson.name.trim()) return 'Enter their name';
    if (!newPerson.email.trim()) return 'Enter an email';
    if (newPerson.password.length < 6) return 'The password needs at least 6 characters';
    return null;
  })();

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }
  if (!brand) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'Brand not found'}</Alert></Box>;
  }

  const label = (b?: Branch | null) => (b ? b.branchName || b.studioName : '—');

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/brands')} sx={{ mb: 2 }}>
        All brands
      </Button>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      {/* Soft pink wash, logo tile lifted off it, facts as quiet icon pills,
          and the two counts as stat cards that jump to their tab. */}
      <Box
        sx={{
          p: { xs: 2.5, md: 3 }, mb: 3, borderRadius: 4,
          border: `1px solid ${colors.primary}33`,
          background: `linear-gradient(120deg, #fdeef4 0%, #fff7fa 55%, ${colors.white} 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2.5, md: 3 }} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={2.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={brand.logo}
              variant="rounded"
              sx={{
                width: 72, height: 72, borderRadius: 3, fontSize: 24, fontWeight: 800,
                bgcolor: colors.primaryDark, color: '#fff',
                border: `3px solid ${colors.white}`,
                boxShadow: '0 6px 18px rgba(233, 109, 154, 0.3)',
              }}
            >
              {initials(brand.name)}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h4" fontWeight={800} noWrap sx={{ letterSpacing: '-0.5px', fontSize: { xs: '1.35rem', md: '1.6rem' } }}>
                  {brand.name}
                </Typography>
                <Chip
                  size="small"
                  label={brand.status === 'suspended' ? 'Suspended' : 'Active'}
                  sx={brand.status === 'suspended'
                    ? { height: 22, fontWeight: 600, bgcolor: '#fff3e0', color: '#e65100' }
                    : { height: 22, fontWeight: 600, bgcolor: '#e6f4e7', color: '#2e7d32' }}
                />
              </Stack>

              <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {[
                  { icon: <LinkIcon sx={{ fontSize: 15 }} />, label: `/${brand.slug}`, tip: 'Brand slug' },
                  {
                    icon: <PhoneIphone sx={{ fontSize: 15 }} />,
                    label: brand.appSource || 'No app of its own',
                    tip: 'App source',
                  },
                  {
                    icon: <CreditCard sx={{ fontSize: 15 }} />,
                    label: brand.hasOwnGateway ? 'Own Razorpay' : 'SweatSocial Razorpay',
                    tip: 'Payments',
                    good: brand.hasOwnGateway,
                  },
                ].map((m) => (
                  <Tooltip key={m.tip} title={m.tip}>
                    <Stack
                      direction="row" spacing={0.5} alignItems="center"
                      sx={{
                        px: 1.25, py: 0.4, borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                        bgcolor: m.good ? '#e6f4e7' : colors.white,
                        color: m.good ? '#2e7d32' : colors.textSecondary,
                        border: `1px solid ${m.good ? 'transparent' : colors.divider}`,
                      }}
                    >
                      {m.icon}
                      <span>{m.label}</span>
                    </Stack>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ '& > *': { flex: { xs: 1, md: 'none' } } }}>
            {[
              { n: branches.length, label: branches.length === 1 ? 'Location' : 'Locations', icon: <Storefront fontSize="small" />, to: 0 },
              { n: activeGrants.length, label: 'With access', icon: <Group fontSize="small" />, to: 2 },
            ].map((s) => (
              <Box
                key={s.label}
                onClick={() => setTab(s.to)}
                sx={{
                  pl: 0.75, pr: 1.75, py: 0.75, borderRadius: 999, cursor: 'pointer',
                  bgcolor: colors.white, border: `1px solid ${colors.divider}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                  transition: 'border-color .15s, box-shadow .15s',
                  '&:hover': { borderColor: colors.primaryLight, boxShadow: '0 2px 10px rgba(244, 143, 180, 0.2)' },
                }}
              >
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: '#fdeef4', color: colors.primaryDark, '& svg': { fontSize: 16 },
                }}>
                  {s.icon}
                </Box>
                <Typography variant="body2" fontWeight={800} sx={{ color: colors.textPrimary }}>{s.n}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{s.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Locations (${branches.length})`} />
        <Tab label="Sharing" />
        <Tab label={`Access (${activeGrants.length})`} />
        <Tab label="Settings" />
      </Tabs>

      {/* ── LOCATIONS ─────────────────────────────────────────────────────── */}
      {tab === 0 && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2.5 }}>
            <Typography variant="h5" fontWeight={700}>Locations</Typography>
            <Stack direction="row" spacing={1} sx={{ '& > *': { flex: { xs: 1, sm: 'none' } } }}>
              <Button
                startIcon={<PlaylistAdd />}
                onClick={openAdopt}
                sx={{
                  borderRadius: 999, px: 2.5, color: colors.primaryDark,
                  bgcolor: `${colors.primary}26`, '&:hover': { bgcolor: `${colors.primary}40` },
                }}
              >
                Add existing
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={openNewBranch} sx={{ borderRadius: 999, px: 2.5 }}>
                New location
              </Button>
            </Stack>
          </Stack>

          {branches.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{ textAlign: 'center', py: 7, borderRadius: 3, borderStyle: 'dashed', bgcolor: colors.backgroundTertiary }}
            >
              <Storefront sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
              <Typography fontWeight={600} gutterBottom>
                No locations yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create one, or move a studio that already exists into this brand.
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button variant="outlined" startIcon={<PlaylistAdd />} onClick={openAdopt}>
                  Add existing studio
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={openNewBranch}>
                  New location
                </Button>
              </Stack>
            </Paper>
          ) : (
            /*
              A LIST, not a grid of cards.
              
              A location carries five facts and no picture. An operator scans
              this to find one name, compare two addresses, or see at a glance
              which one the app opens on — all things a row does well and a
              card grid does badly, because the eye has to re-find each field
              in every tile.
            */
            <Box sx={{ border: `1px solid ${colors.divider}`, borderRadius: 3, overflow: 'hidden', bgcolor: colors.white }}>
              {/* Column headings — desktop only; on a phone each row is a card. Same as Access. */}
              <Box sx={{
                display: { xs: 'none', md: 'grid' }, gridTemplateColumns: LOCATION_GRID, gap: 2,
                px: 2.5, py: 1.25, bgcolor: colors.backgroundTertiary, borderBottom: `1px solid ${colors.divider}`,
              }}>
                {['Location', 'City', 'Login', ''].map((h) => (
                  <Typography key={h} variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    {h}
                  </Typography>
                ))}
              </Box>

              {branches.map((b) => {
                const isDefault = String(brand.defaultStudio) === String(b._id);
                const off = b.branchIsActive === false;
                const name = b.branchName || b.studioName;
                const address = [b.address, b.city].filter(Boolean).join(', ');
                return (
                  <Box
                    key={b._id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: LOCATION_GRID },
                      gridTemplateAreas: { xs: '"loc actions" "addr addr" "login login"', md: '"loc addr login actions"' },
                      rowGap: { xs: 1, md: 0 }, columnGap: 2, alignItems: 'center',
                      px: 2.5, py: 1.5,
                      '&:not(:last-child)': { borderBottom: `1px solid ${colors.divider}` },
                      opacity: off ? 0.55 : 1,
                      '&:hover': { bgcolor: colors.backgroundTertiary },
                    }}
                  >
                    {/* Tile matches the location tiles on the Access tab;
                        the default one is the solid version. */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ gridArea: 'loc', minWidth: 0 }}>
                      <Box sx={{
                        width: 38, height: 38, borderRadius: 2, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
                        bgcolor: isDefault ? colors.primaryDark : '#fdeef4',
                        color: isDefault ? '#fff' : colors.primaryDark,
                      }}>
                        {(b.branchCode || initials(name)).toUpperCase().slice(0, 3)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Typography fontWeight={600} noWrap>{name}</Typography>
                          {isDefault && (
                            <Tooltip title="The app opens here, and this location represents the brand in the marketplace">
                              <Chip
                                size="small"
                                icon={<Star sx={{ fontSize: '13px !important', color: `${colors.primaryDark} !important` }} />}
                                label="Opens here"
                                sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: `${colors.primary}26`, color: colors.primaryDark }}
                              />
                            </Tooltip>
                          )}
                          {off && <Chip size="small" label="Inactive" sx={{ height: 20, fontSize: 11 }} />}
                        </Stack>
                        {b.branchName && b.branchName !== b.studioName && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">{b.studioName}</Typography>
                        )}
                      </Box>
                    </Stack>

                    {/* City only — the full address is on hover, not typed out in the column. */}
                    <Tooltip title={address}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ gridArea: 'addr', minWidth: 0, width: 'fit-content', maxWidth: '100%' }}>
                        <Place sx={{ fontSize: 16, color: colors.primary, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap color={b.city ? 'text.primary' : 'text.disabled'}>
                          {b.city || '—'}
                        </Typography>
                      </Stack>
                    </Tooltip>

                    <Box sx={{ gridArea: 'login' }}>
                      <Tooltip title={b.email ? b.email : 'No sign-in — people reach it through an access grant'}>
                        <Chip
                          size="small"
                          icon={b.email ? <VpnKey sx={{ fontSize: '13px !important' }} /> : undefined}
                          label={b.email ? 'Own login' : 'No login'}
                          sx={b.email
                            ? { fontWeight: 600, bgcolor: '#e6f4e7', color: '#2e7d32', '& .MuiChip-icon': { color: '#2e7d32' } }
                            : { fontWeight: 600, bgcolor: colors.backgroundTertiary, color: colors.textSecondary, border: `1px solid ${colors.divider}` }}
                        />
                      </Tooltip>
                    </Box>

                    <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center" sx={{ gridArea: 'actions' }}>
                      <Tooltip title="Open location">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/studios/${b._id}`)}
                          sx={{ color: colors.primaryDark, bgcolor: `${colors.primary}1F`, '&:hover': { bgcolor: `${colors.primary}40` } }}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More">
                        <IconButton
                          size="small"
                          onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuBranch(b); }}
                          sx={{ color: colors.textSecondary, bgcolor: colors.backgroundTertiary, '&:hover': { bgcolor: colors.divider } }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          )}

          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            {menuBranch && String(brand.defaultStudio) !== String(menuBranch._id) && (
              <MenuItem onClick={() => { patch({ defaultStudio: menuBranch._id }, 'Default location changed'); setMenuAnchor(null); }}>
                <StarBorder fontSize="small" sx={{ mr: 1 }} /> Make default
              </MenuItem>
            )}
            <MenuItem onClick={() => { if (menuBranch) duplicate(menuBranch); setMenuAnchor(null); }}>
              <ContentCopy fontSize="small" sx={{ mr: 1 }} /> Duplicate
            </MenuItem>
            <MenuItem onClick={() => { if (menuBranch) openLogin(menuBranch); setMenuAnchor(null); }}>
              <Password fontSize="small" sx={{ mr: 1 }} /> {menuBranch?.email ? 'Reset login password' : 'Create login'}
            </MenuItem>
            <MenuItem onClick={() => { if (menuBranch) toggleBranch(menuBranch); setMenuAnchor(null); }}>
              {menuBranch?.branchIsActive === false ? 'Activate' : 'Deactivate'}
            </MenuItem>
            {branches.length > 1 && (
              <MenuItem onClick={() => { setDetaching(menuBranch); setMenuAnchor(null); }}>
                <CallSplit fontSize="small" sx={{ mr: 1 }} /> Remove from brand
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      {/* ── SHARING ───────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <BrandSharingTab
          sharing={brand.sharing}
          locationCount={branches.length}
          onError={setError}
          onSave={async (changes) => {
            setError(null);
            const res: any = await apiService.updateBrand(brandId, { sharing: changes } as any);
            setBrand(res.brand);
            setBranches(res.studios ?? []);
            setNotice('Sharing saved');
          }}
        />
      )}

      {/* ── ACCESS ────────────────────────────────────────────────────────── */}
      {tab === 2 && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={1.5} sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Team access</Typography>
            </Box>
            <Button variant="contained" startIcon={<LockOpen />} onClick={openGrant} sx={{ borderRadius: 999, px: 3, flexShrink: 0 }}>
              Grant access
            </Button>
          </Stack>

          <BrandAccessList
            people={accessPeople}
            showRevoked={showRevoked}
            onEditLocations={(p) => openLocationEditor(p as AccessPerson)}
            onRemove={(p) => setRemovingAll(p as AccessPerson)}
          />

          {accessPeople.some((p) => p.activeCount === 0) && (
            <Button size="small" sx={{ mt: 1.5 }} onClick={() => setShowRevoked((v) => !v)}>
              {showRevoked ? 'Hide' : 'Show'}{' '}
              {accessPeople.filter((p) => p.activeCount === 0).length} revoked
            </Button>
          )}
        </>
      )}

      {/* ── SETTINGS ──────────────────────────────────────────────────────── */}
      {tab === 3 && (
        <BrandSettingsTab
          brand={brand}
          whitelabel={brandWhitelabel}
          locationCount={branches.length}
          onError={setError}
          onSaveBrand={async (data) => {
            setError(null);
            const res: any = await apiService.updateBrand(brandId, data);
            setBrand(res.brand);
            setBranches(res.studios ?? []);
            setNotice('Settings saved');
          }}
          onSaveWhitelabel={async (fields) => {
            setError(null);
            await apiService.setBrandWhitelabel(brandId, fields);
            setNotice('Settings saved');
            await load();
          }}
        />
      )}

      {/* ── NEW LOCATION ──────────────────────────────────────────────────── */}
      <Dialog open={branchOpen} onClose={() => setBranchOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 0.5 }}>
          {duplicateOf ? `Duplicate ${duplicateOf.branchName || duplicateOf.studioName}` : 'New location'}
        </DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
          {duplicateOf
            ? 'Address and contact details copied over. Give it its own name and code — nothing is saved until you do.'
            : 'Inherits this brand\u2019s app, settings and policies. It needs no login of its own.'}
        </Typography>
        <Divider />
        <DialogContent>
          {/* Errors belong next to the form that caused them: the page-level
              alert sits behind this dialog, so a refused save read as a dead
              button. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Studio name" value={branchForm.studioName} autoFocus fullWidth
              placeholder="Purple Pilates Indiranagar"
              onChange={(e) => setBranchForm({ ...branchForm, studioName: e.target.value })}
            />
            {branches.length === 0 && (
              // The brand's FIRST location has no default to copy a type from.
              <FormControl fullWidth>
                <InputLabel>Studio type</InputLabel>
                <Select
                  label="Studio type"
                  value={branchForm.studioType}
                  onChange={(e) =>
                    setBranchForm({ ...branchForm, studioType: e.target.value as 'workout' | 'wellness' })
                  }
                >
                  <MenuItem value="workout">Workout</MenuItem>
                  <MenuItem value="wellness">Wellness</MenuItem>
                </Select>
              </FormControl>
            )}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Location name" value={branchForm.branchName} fullWidth placeholder="Indiranagar"
                onChange={(e) => setBranchForm({ ...branchForm, branchName: e.target.value })}
              />
              <TextField
                label="Code" value={branchForm.branchCode} sx={{ width: 130 }} placeholder="IND"
                onChange={(e) => setBranchForm({ ...branchForm, branchCode: e.target.value })}
              />
            </Stack>
            <TextField
              label="Address" value={branchForm.address} fullWidth
              onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
            />
            <Stack direction="row" spacing={2}>
              <TextField label="City" value={branchForm.city} fullWidth
                onChange={(e) => setBranchForm({ ...branchForm, city: e.target.value })} />
              <TextField label="Phone" value={branchForm.phoneNumber} fullWidth
                onChange={(e) => setBranchForm({ ...branchForm, phoneNumber: e.target.value })} />
            </Stack>

            <Divider />
            {/*
              The old form REQUIRED an email and password, because a branch was
              a second studio login. It is now the exception: the people who
              work at a location are granted access to it on the Access tab.
            */}
            <Box>
              <FormControlLabel
                control={<Switch checked={wantsLogin} onChange={(e) => setWantsLogin(e.target.checked)} />}
                label="This location needs its own separate login"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                Only for a franchisee who is not staff of the brand. Otherwise leave
                it off and grant people access instead.
              </Typography>
            </Box>
            {wantsLogin && (
              <Stack direction="row" spacing={2}>
                <TextField id="branch-login-email" label="Email" value={branchForm.email} fullWidth disabled={saving}
                  onChange={(e) => setBranchForm({ ...branchForm, email: e.target.value })} />
                <TextField id="branch-login-password" label="Password (optional)" type="password" value={branchForm.password} fullWidth disabled={saving}
                  helperText="Leave blank and we’ll generate a strong one"
                  onChange={(e) => setBranchForm({ ...branchForm, password: e.target.value })} />
              </Stack>
            )}

            {branches.length > 0 && (
              <>
                <Divider />
                <Box>
                  <FormControlLabel
                    control={<Switch checked={copyPayments} disabled={saving} onChange={(e) => setCopyPayments(e.target.checked)} />}
                    label="Collect payments into the same Razorpay account as the default location"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                    {copyPayments
                      ? 'Online payments at this location go into the default location’s Razorpay account.'
                      : 'Off: this location collects through SweatSocial’s Razorpay account until its own keys are added in its settings. Email login details are never copied; mail goes out through SweatSocial.'}
                  </Typography>
                </Box>
              </>
            )}
            {branchError && <Alert ref={branchErrorRef} severity="error">{branchError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBranchOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained" onClick={createBranch}
            disabled={saving || !branchForm.studioName.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Creating location…' : 'Add location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── LOCATION LOGIN: create / reset password ─────────────────────────── */}
      <Dialog open={!!loginFor} onClose={loginSaving ? undefined : () => setLoginFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{loginFor?.email ? 'Reset login password' : 'Create login'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {loginFor?.email
                ? <>A new password for <b>{loginFor?.email}</b>, the owner login of <b>{loginFor?.branchName || loginFor?.studioName}</b>. The old password stops working straight away.</>
                : <>An owner login for <b>{loginFor?.branchName || loginFor?.studioName}</b>. It can sign in to the admin panel and partner app for this location only.</>}
            </Typography>
            {!loginFor?.email && (
              <TextField id="branch-login-new-email" label="Email" value={loginForm.email} disabled={loginSaving} fullWidth
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
            )}
            <ToggleButtonGroup size="small" exclusive value={loginForm.mode} disabled={loginSaving}
              onChange={(_, v) => v && setLoginForm({ ...loginForm, mode: v })}>
              <ToggleButton value="generate">Generate a password</ToggleButton>
              <ToggleButton value="type">Type one</ToggleButton>
            </ToggleButtonGroup>
            {loginForm.mode === 'type' && (
              <TextField id="branch-login-new-password" label="New password" type="password" value={loginForm.password} disabled={loginSaving} fullWidth
                helperText="At least 8 characters"
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
            )}
            {loginError && <Alert severity="error">{loginError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLoginFor(null)} disabled={loginSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveLogin} disabled={loginSaving}
            startIcon={loginSaving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {loginSaving ? 'Saving…' : loginFor?.email ? 'Reset password' : 'Create login'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CREDENTIALS, SHOWN ONCE ──────────────────────────────────────────── */}
      <Dialog open={!!credentials} onClose={() => setCredentials(null)} fullWidth maxWidth="xs">
        <DialogTitle>{credentials?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Owner login for <b>{credentials?.location}</b>:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, display: 'grid', gap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" sx={{ width: 72, color: 'text.secondary' }}>Email</Typography>
                <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>{credentials?.email}</Typography>
                <Tooltip title={copied === 'email' ? 'Copied' : 'Copy email'}>
                  <IconButton size="small" onClick={() => credentials && copy('email', credentials.email)}><ContentCopy fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
              {credentials?.password && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ width: 72, color: 'text.secondary' }}>Password</Typography>
                  <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}>{credentials.password}</Typography>
                  <Tooltip title={copied === 'password' ? 'Copied' : 'Copy password'}>
                    <IconButton size="small" onClick={() => copy('password', credentials.password!)}><ContentCopy fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Paper>
            {credentials?.password && (
              <Alert severity="warning">
                This password is shown only once. Copy it now and share it with the location owner privately. You can reset it any time from the location’s menu.
              </Alert>
            )}
            {credentials?.note && (
              <Typography variant="body2" color="text.secondary">{credentials.note}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {credentials?.password && (
            <Button onClick={() => credentials && copy('both', `Email: ${credentials.email}\nPassword: ${credentials.password}`)}>
              {copied === 'both' ? 'Copied' : 'Copy both'}
            </Button>
          )}
          <Button variant="contained" onClick={() => setCredentials(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ── ADOPT EXISTING ────────────────────────────────────────────────── */}
      <Dialog open={adoptOpen} onClose={() => setAdoptOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 0.5 }}>Add an existing studio</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
          For a studio that was created on its own and turns out to be part of
          this business. Its members, packs and staff move with it.
        </Typography>
        <Divider />
        <DialogContent>
          {/* Errors belong next to the form that caused them: the page-level
              alert sits behind this dialog, so a refused save read as a dead
              button. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Autocomplete
            sx={{ mt: 1 }}
            options={adoptable}
            value={adoptPick}
            onChange={(_, v) => setAdoptPick(v)}
            inputValue={adoptSearch}
            onInputChange={(_, v) => setAdoptSearch(v)}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o.studioName}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderOption={(props, o) => (
              <li {...props} key={o._id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{o.studioName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[o.city, o.appSource].filter(Boolean).join(' · ') || 'no city'}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(p) => (
              <TextField {...p} label="Studio" placeholder="Search studios" autoFocus />
            )}
            noOptionsText="No studio is available to move"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Only studios that are not already a location of another multi-location
            brand are listed. To move one of those, remove it from its brand first.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdoptOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={adopt} disabled={saving || !adoptPick}>
            {saving ? 'Moving…' : 'Add to brand'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── GRANT ─────────────────────────────────────────────────────────── */}
      <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Grant access</DialogTitle>
        <Divider />
        <DialogContent>
          {/* Errors belong next to the form that caused them: the page-level
              alert sits behind this dialog, so a refused save read as a dead
              button. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={grantForm.role}
                onChange={(e) => setGrantForm({ ...grantForm, role: e.target.value as Grant['role'] })}>
                <MenuItem value="brand_owner">Brand owner &mdash; every location</MenuItem>
                <MenuItem value="studio_owner">Studio owner &mdash; one location</MenuItem>
                <MenuItem value="manager">Manager &mdash; one location</MenuItem>
                <MenuItem value="instructor">Instructor &mdash; one location</MenuItem>
              </Select>
            </FormControl>

            {grantForm.role === 'brand_owner' ? (
              <Alert severity="info">
                A brand owner covers every location, including ones added later.
                There is no location to pick.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Locations</InputLabel>
                <Select
                  multiple
                  label="Locations"
                  value={grantStudios}
                  onChange={(e) =>
                    setGrantStudios(
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : (e.target.value as string[]),
                    )
                  }
                  renderValue={(sel) =>
                    (sel as string[])
                      .map((id) => label(branches.find((b) => b._id === id)))
                      .join(', ')
                  }
                >
                  {branches.map((b) => (
                    <MenuItem key={b._id} value={b._id}>
                      <Checkbox checked={grantStudios.includes(b._id)} size="small" />
                      <ListItemText
                        primary={label(b)}
                        secondary={[b.branchCode, b.city].filter(Boolean).join(' · ') || undefined}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {grantStudios.length > 1
                    ? `${grantStudios.length} locations — one grant is written for each, so any of them can be revoked on its own.`
                    : 'Someone who runs several locations can be given all of them at once.'}
                </FormHelperText>
              </FormControl>
            )}

            <Divider />

            {/*
              This was a free-text ObjectId field. A super admin had no way to
              obtain that id from the panel — they had to read it out of the
              database — so the one control that creates a brand owner could
              not actually be used from the product.
            */}
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={grantMode}
              onChange={(_, v) => v && setGrantMode(v)}
            >
              <ToggleButton value="pick">Someone who already has a login</ToggleButton>
              <ToggleButton value="new">Create a new login</ToggleButton>
            </ToggleButtonGroup>

            {grantMode === 'pick' ? (
              <Autocomplete
                options={people}
                value={person}
                onChange={(_, v) => setPerson(v)}
                getOptionLabel={(o) => `${o.name} (${o.email})`}
                isOptionEqualToValue={(a, b) => a.id === b.id && a.kind === b.kind}
                groupBy={(o) => (o.kind === 'studio' ? 'Studio accounts' : 'Staff')}
                renderOption={(props, o) => (
                  <li {...props} key={`${o.kind}:${o.id}`}>
                    <Box sx={{ width: '100%' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                          {o.name}
                        </Typography>
                        {o.current && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${ROLE_LABEL[o.current.role] ?? o.current.role} · ${o.current.where}`}
                          />
                        )}
                        {!o.isActive && <Chip size="small" color="warning" label="inactive" />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {o.email}
                        {o.locations.length ? ` · ${o.locations.join(', ')}` : ''}
                        {o.note ? ` · ${o.note}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField {...p} label="Person" placeholder="Search by name or email" />
                )}
                noOptionsText="Nobody yet — create a login instead"
              />
            ) : (
              <Stack spacing={2}>
                <TextField
                  label="Full name" value={newPerson.name} fullWidth
                  name="brand-grant-name" autoComplete="off"
                  onChange={(e) => setNewPerson({ ...newPerson, name: e.target.value })}
                />
                <TextField
                  label="Email" value={newPerson.email} fullWidth
                  name="brand-grant-email" autoComplete="off"
                  onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
                  helperText="This is what they sign in with."
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Password" type="password" value={newPerson.password} fullWidth
                    // Chrome autofills a saved login into any email+password
                    // pair. Autofill writes the DOM without firing onChange,
                    // so a controlled field can look full while state is
                    // empty. `new-password` is what Chrome honours here.
                    name="brand-grant-password" autoComplete="new-password"
                    onChange={(e) => setNewPerson({ ...newPerson, password: e.target.value })}
                    error={!!newPerson.password && newPerson.password.length < 6}
                    helperText={
                      newPerson.password && newPerson.password.length < 6
                        ? `${6 - newPerson.password.length} more to go`
                        : 'At least 6 characters.'
                    }
                  />
                  <TextField
                    label="Phone (optional)" value={newPerson.phone} fullWidth
                    onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })}
                  />
                </Stack>
              </Stack>
            )}

            {person?.current && (
              <Alert severity={person.current.role === grantForm.role ? 'info' : 'warning'}>
                {person.name} is already a{' '}
                <b>{ROLE_LABEL[person.current.role] ?? person.current.role}</b> at{' '}
                {person.current.where}.{' '}
                {person.current.role === grantForm.role
                  ? 'Granting the same role at another location is fine.'
                  : 'One person holds one role per brand — revoke that grant first, or this will be refused.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGrantOpen(false)}>Cancel</Button>
          {/*
            This checked `grantForm.userId`, which stopped being set the moment
            the raw ObjectId field became a picker — so the button was disabled
            no matter what anyone typed. A disabled control with no stated
            reason is the worst version of that: the operator reads a filled-in
            form and a dead button. The tooltip now names what is missing.
          */}
          <Tooltip title={grantBlockedBecause ?? ''} placement="top">
            <span>
              <Button
                variant="contained"
                onClick={submitGrant}
                disabled={saving || !!grantBlockedBecause}
              >
                {saving ? 'Granting…' : 'Grant'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Dialog open={!!revoking} onClose={() => setRevoking(null)}>
        <DialogTitle>Revoke access?</DialogTitle>
        <DialogContent>
          {/* Errors belong next to the form that caused them: the page-level
              alert sits behind this dialog, so a refused save read as a dead
              button. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            They lose access immediately. The record is kept as revoked rather
            than deleted, so re-granting restores the same scope.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={revoke}>Revoke</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editingLocations} onClose={() => setEditingLocations(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 0.5 }}>
          {editingLocations?.name}&rsquo;s locations
        </DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
          {ROLE_LABEL[editingLocations?.role ?? ''] ?? editingLocations?.role} at
          whichever you tick. Unticking one revokes that location and nothing else.
        </Typography>
        <Divider />
        <DialogContent>
          {/* Errors belong next to the form that caused them: the page-level
              alert sits behind this dialog, so a refused save read as a dead
              button. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack sx={{ mt: 1 }}>
            {branches.map((b) => (
              <FormControlLabel
                key={b._id}
                control={
                  <Checkbox
                    checked={editLocations.includes(b._id)}
                    onChange={(e) =>
                      setEditLocations((prev) =>
                        e.target.checked
                          ? [...prev, b._id]
                          : prev.filter((x) => x !== b._id),
                      )
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{label(b)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[b.branchCode, b.city].filter(Boolean).join(' · ') || b.studioName}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Stack>
          {!editLocations.length && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              With none ticked they keep their login but reach nothing here.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingLocations(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveLocations} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!removingAll} onClose={() => setRemovingAll(null)}>
        <DialogTitle>Remove {removingAll?.name}&rsquo;s access?</DialogTitle>
        <DialogContent>
          {/* Errors belong next to the form that caused them: the page-level
              alert sits behind this dialog, so a refused save read as a dead
              button. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            {removingAll?.activeCount === 1
              ? 'Their one grant in this brand is revoked.'
              : `All ${removingAll?.activeCount} of their grants in this brand are revoked.`}{' '}
            The login itself is untouched — they can still sign in, they just
            reach nothing here until access is granted again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingAll(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeAllAccess}>
            Remove access
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detaching} onClose={() => setDetaching(null)}>
        <DialogTitle>Remove from this brand?</DialogTitle>
        <DialogContent>
          {/* Errors belong next to the form that caused them: the page-level
              alert sits behind this dialog, so a refused save read as a dead
              button. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            <b>{detaching?.studioName}</b> gets a brand of its own. Its members,
            packs and staff go with it, and it stops sharing anything with the
            other locations. Nothing is deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetaching(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={detach}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notice}
        autoHideDuration={3500}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setNotice(null)}>{notice}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandDetailPage;
